
import { forwardRef, TextareaHTMLAttributes, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { formFieldFocusSurface, formFieldRestSurface } from './formFieldStyles';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  showCharCount?: boolean;
  maxLength?: number;
  autoExpand?: boolean;
  variant?: 'default' | 'borderless';
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      showCharCount = false,
      maxLength,
      autoExpand = true,
      variant = 'default',
      value,
      id,
      required,
      onChange,
      ...props
    },
    ref
  ) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    const charCount = typeof value === 'string' ? value.length : 0;

    // Auto-expand textarea based on content
    useEffect(() => {
      if (autoExpand && textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }, [value, autoExpand, textareaRef]);

    return (
      <div className="w-full">
        <div className="flex justify-between items-baseline mb-1.5">
          {label && (
            <label
              htmlFor={textareaId}
              className="block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {label}
              {required && (
                <span className="text-red-500 dark:text-red-400 ml-0.5" aria-hidden="true">
                  *
                </span>
              )}
            </label>
          )}
          {showCharCount && maxLength && (
            <span
              className={cn(
                'text-xs',
                charCount > maxLength * 0.9
                  ? charCount >= maxLength
                    ? 'text-red-500'
                    : 'text-amber-500'
                  : 'text-slate-400'
              )}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
        <textarea
          ref={textareaRef}
          id={textareaId}
          value={value}
          onChange={onChange}
          required={required}
          maxLength={maxLength}
          rows={4}
          className={cn(
            'block w-full text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500',
            'resize-none transition-colors duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            variant === 'borderless'
              ? cn(
                  'rounded-lg px-4 py-3 text-lg leading-relaxed min-h-[140px] border-0',
                  'transition-all duration-200',
                  formFieldRestSurface,
                  formFieldFocusSurface,
                  error && 'ring-red-500 focus:ring-red-500'
                )
              : cn(
                  'rounded-lg border bg-white dark:bg-slate-800',
                  'px-4 py-3 text-base min-h-[120px]',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                  'disabled:bg-slate-50 dark:disabled:bg-slate-900',
                  error
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
                ),
            className
          )}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
          }
          {...props}
        />
        {error && (
          <p
            id={`${textareaId}-error`}
            className="mt-1.5 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p
            id={`${textareaId}-helper`}
            className="mt-1.5 text-sm text-slate-500 dark:text-slate-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
